import { useEffect, useState, type ReactNode } from 'react';
import { api, errorMessage } from '../api';
import { PERMISSIONS } from '../constants';
import { formatDateTime } from '../format';
import { useAsyncData } from '../useAsyncData';
import type {
  CloudPlayer,
  PlayersResponse,
  RankResponse,
  Role,
  RolesResponse,
  ToastKind,
  UserWithRole,
  UsersResponse,
} from '../types';
import { EmptyBox, ErrorBox, HintBox, Loading } from '../components/Feedback';
import { UserCreateDialog } from '../components/UserCreateDialog';
import { UserEditDialog } from '../components/UserEditDialog';
import { ColorDot } from '../components/UserBadge';

export interface TeamPageProps {
  permissions: string[];
  revision: number;
  currentUserId: string;
  onToast: (kind: ToastKind, text: string) => void;
}

export function TeamPage({ permissions, revision, currentUserId, onToast }: TeamPageProps): ReactNode {
  const canManageUsers = permissions.includes(PERMISSIONS.USER_MANAGE);
  const canViewPlayers = permissions.includes(PERMISSIONS.TASK_VIEW);
  const canManageRanks = permissions.includes(PERMISSIONS.ROLE_MANAGE);

  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const usersState = useAsyncData(() => api.get<UsersResponse>('/api/users/all'), [revision], canManageUsers);
  const rolesState = useAsyncData(
    () => api.get<RolesResponse>('/api/roles'),
    [revision],
    canManageUsers || canViewPlayers,
  );
  const playersState = useAsyncData(
    () => api.get<PlayersResponse>('/api/players'),
    [revision],
    canViewPlayers,
  );

  if (!canManageUsers) {
    return <ErrorBox message="Für die Teamverwaltung fehlt das Recht user.manage." />;
  }

  const users = usersState.data?.users ?? [];
  const roles: Role[] = rolesState.data?.roles ?? [];
  const players = playersState.data?.players ?? [];
  const editingUser = editingId === null ? null : users.find((user) => user.id === editingId) ?? null;

  return (
    <div className="page">
      <div className="board-toolbar">
        <div className="toolbar-left">
          <h2 className="page-title">Team</h2>
          <span className="muted small">
            {users.length} Benutzer · {users.filter((user) => user.active).length} aktiv
          </span>
        </div>
        <div className="toolbar-right">
          <button
            type="button"
            className="button button-ghost button-small"
            onClick={() => usersState.reload()}
            disabled={usersState.loading}
          >
            Aktualisieren
          </button>
          <button type="button" className="button button-primary" onClick={() => setCreating(true)}>
            + Neuer Benutzer
          </button>
        </div>
      </div>

      {usersState.error !== null && <ErrorBox message={usersState.error} onRetry={() => usersState.reload()} />}
      {rolesState.error !== null && <HintBox text={`Rollen konnten nicht geladen werden: ${rolesState.error}`} />}
      {usersState.loading && usersState.data === null && <Loading label="Benutzer laden …" />}

      {users.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Benutzer</th>
                <th>Anzeigename</th>
                <th>Rolle</th>
                <th>Status</th>
                <th>Quelle</th>
                <th>Rolle gesperrt</th>
                <th>Letzter Login</th>
                <th aria-label="Aktionen" />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className={user.active ? undefined : 'row-inactive'}>
                  <td>
                    <span className="mono">@{user.username}</span>
                    {user.id === currentUserId && <span className="badge badge-muted">Du</span>}
                  </td>
                  <td>{user.displayName}</td>
                  <td>
                    <span className="role-cell">
                      <ColorDot color={user.role?.color} title={user.role?.displayName ?? 'Keine Rolle'} />
                      {user.role?.displayName ?? <span className="muted">Keine Rolle</span>}
                    </span>
                  </td>
                  <td>
                    <span className={user.active ? 'badge badge-ok' : 'badge badge-off'}>
                      {user.active ? 'aktiv' : 'inaktiv'}
                    </span>
                  </td>
                  <td className="muted small">{user.source}</td>
                  <td>{user.roleLocked ? 'ja' : <span className="muted">nein</span>}</td>
                  <td className="muted small">{formatDateTime(user.lastLoginAt)}</td>
                  <td className="cell-actions">
                    <button
                      type="button"
                      className="button button-ghost button-small"
                      onClick={() => setEditingId(user.id)}
                    >
                      Bearbeiten
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!usersState.loading && usersState.error === null && users.length === 0 && (
        <EmptyBox text="Keine Benutzer vorhanden." />
      )}

      <section className="section-block">
        <div className="detail-section-head">
          <h3>Spieler-Rollen aus der Cloud</h3>
          <span className="muted small">
            {canManageRanks
              ? 'Ränge können hier direkt gesetzt werden.'
              : 'Nur lesbar — zum Setzen fehlt das Recht role.manage.'}
          </span>
        </div>

        {!canViewPlayers && (
          <HintBox text="Für die Spielerliste fehlt das Recht task.view." />
        )}
        {playersState.error !== null && (
          <ErrorBox message={playersState.error} onRetry={() => playersState.reload()} />
        )}
        {playersState.loading && playersState.data === null && <Loading label="Spieler laden …" />}
        {canViewPlayers && !playersState.loading && playersState.error === null && players.length === 0 && (
          <EmptyBox text="Die Cloud hat noch keine Spieler-Rollen gemeldet." />
        )}

        {players.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Spieler</th>
                  <th>Aktuelle Rolle</th>
                  <th>Zuletzt aktualisiert</th>
                  <th>Neuer Rang</th>
                </tr>
              </thead>
              <tbody>
                {players.map((player) => (
                  <PlayerRankRow
                    key={player.uuid}
                    player={player}
                    roles={roles}
                    canManageRanks={canManageRanks}
                    onToast={onToast}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {creating && (
        <UserCreateDialog
          roles={roles}
          onClose={() => setCreating(false)}
          onCreated={(message) => {
            setCreating(false);
            onToast('success', message);
            usersState.reload();
          }}
        />
      )}

      {editingUser !== null && (
        <UserEditDialog
          user={editingUser}
          roles={roles}
          isSelf={editingUser.id === currentUserId}
          onClose={() => setEditingId(null)}
          onSaved={(message) => {
            onToast('success', message);
            usersState.reload();
          }}
          onError={(message) => onToast('error', message)}
        />
      )}
    </div>
  );
}

interface PlayerRankRowProps {
  player: CloudPlayer;
  roles: Role[];
  canManageRanks: boolean;
  onToast: (kind: ToastKind, text: string) => void;
}

function PlayerRankRow({ player, roles, canManageRanks, onToast }: PlayerRankRowProps): ReactNode {
  const [roleId, setRoleId] = useState(player.roleId ?? '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRoleId(player.roleId ?? '');
  }, [player.roleId, player.uuid]);

  const uuidUnknown = player.uuid.startsWith('name:');
  const currentRole = player.roleId === null ? null : roles.find((role) => role.id === player.roleId) ?? null;

  const submit = async (): Promise<void> => {
    if (roleId === '') {
      onToast('error', 'Bitte zuerst eine Rolle auswählen.');
      return;
    }
    setBusy(true);
    try {
      const result = await api.post<RankResponse>('/api/players/rank', {
        username: player.username,
        rank: roleId,
        ...(uuidUnknown ? {} : { uuid: player.uuid }),
      });
      onToast('success', result.note === '' ? `Rang "${roleId}" für ${player.username} gesetzt.` : result.note);
    } catch (cause: unknown) {
      onToast('error', errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td>
        <span className="mono">{player.username}</span>
        {uuidUnknown && <span className="badge badge-muted">keine UUID</span>}
      </td>
      <td>
        <span className="role-cell">
          <ColorDot color={currentRole?.color} title={currentRole?.displayName ?? 'Keine Rolle'} />
          {currentRole?.displayName ?? player.roleId ?? <span className="muted">unbekannt</span>}
        </span>
      </td>
      <td className="muted small">{formatDateTime(player.updatedAt)}</td>
      <td>
        <span className="rank-controls">
          <select
            value={roleId}
            disabled={!canManageRanks || busy}
            onChange={(event) => setRoleId(event.target.value)}
          >
            <option value="">Rolle wählen …</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.displayName} ({role.name})
              </option>
            ))}
          </select>
          <button
            type="button"
            className="button button-primary button-small"
            disabled={!canManageRanks || busy}
            title={
              canManageRanks
                ? undefined
                : 'Zum Setzen von Rängen fehlt das Recht role.manage.'
            }
            onClick={() => void submit()}
          >
            {busy ? 'Sende …' : 'Rang setzen'}
          </button>
        </span>
        {uuidUnknown && (
          <span className="muted small block">
            Der Cloud ist keine UUID bekannt — der Spieler muss sich einmal eingeloggt haben.
          </span>
        )}
      </td>
    </tr>
  );
}
